import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Camera, Upload, Trash2 } from 'lucide-react';
import CameraCapture from '../CameraCapture';
import { useImageViewer } from '../ImageViewerProvider';
import { 
  uploadBookingPhoto, 
  getBookingPhotos, 
  deleteBookingPhoto,
  getBookingPhotoUrl,
  blobUrlToFile,
  type BookingPhoto 
} from '../../services/bookingPhotoService';

interface BookingPhotoCaptureProps {
  isOpen: boolean;
  onClose: () => void;
  vehicleTitle: string;
  vehicleId: string;
  bookingId: string;
  onPhotosUpdated?: () => void;
}

export default function BookingPhotoCapture({
  isOpen,
  onClose,
  vehicleTitle,
  vehicleId,
  bookingId,
  onPhotosUpdated
}: BookingPhotoCaptureProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [photos, setPhotos] = useState<BookingPhoto[]>([]);
  const [uploading, setUploading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState<string[]>([]); // Track which photos are being uploaded
  const { open: openImageViewer } = useImageViewer();
  const fileInputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    console.log('📸 BookingPhotoCapture isOpen changed:', isOpen);
    if (isOpen) {
      console.log('📸 Modal opening, setting animation to true');
      setIsAnimating(true);
      loadExistingPhotos();
      // Mobile/iOS: open live camera immediately (expenses-style), with library upload on that screen
      if (typeof window !== 'undefined' && window.innerWidth < 640) {
        setShowCamera(true);
      }
    } else {
      // Reset state when modal closes
      setPhotos([]);
      setUploadingPhotos([]);
      setShowCamera(false);
    }
  }, [isOpen, bookingId, vehicleId]);

  const loadExistingPhotos = async () => {
    try {
      console.log('📸 Loading existing photos for booking/vehicle:', { bookingId, vehicleId });
      const existingPhotos = await getBookingPhotos(bookingId, vehicleId);
      setPhotos(existingPhotos);
      console.log('📸 Loaded existing booking photos:', existingPhotos.length);
    } catch (error) {
      console.error('❌ Error loading existing booking photos:', error);
    }
  };

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 150);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    console.log('📸 Processing booking photo file uploads:', files.length);
    setUploading(true);

    try {
      // Upload each file
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        try {
          const result = await uploadBookingPhoto({
            bookingId,
            vehicleId,
            file
          });

          if (result.success && result.photo) {
            setPhotos(prev => [...prev, result.photo!]);
            console.log('✅ Booking photo uploaded successfully:', result.photo.id);
          } else {
            console.error('❌ Booking photo upload failed:', result.error);
            alert(`Failed to upload ${file.name}: ${result.error}`);
          }
        } catch (error) {
          console.error('❌ Error uploading booking photo file:', error);
          alert(`Failed to upload ${file.name}`);
        }
      }
      
      if (onPhotosUpdated) {
        onPhotosUpdated();
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCameraCapture = () => {
    setShowCamera(true);
  };

  const handleCameraPhotos = async (newPhotos: string[]) => {
    setShowCamera(false);
    
    if (newPhotos.length === 0) return;

    console.log('📸 Processing booking camera photos:', newPhotos.length);
    setUploading(true);

    try {
      // Upload each photo
      for (let i = 0; i < newPhotos.length; i++) {
        const blobUrl = newPhotos[i];
        const fileName = `booking-photo-${Date.now()}-${i + 1}.jpg`;
        
        // Track this photo as uploading
        setUploadingPhotos(prev => [...prev, blobUrl]);
        
        try {
          // Convert blob URL to File
          const file = await blobUrlToFile(blobUrl, fileName);
          
          // Upload to Supabase
          const result = await uploadBookingPhoto({
            bookingId,
            vehicleId,
            file
          });

          if (result.success && result.photo) {
            // Add to photos list
            setPhotos(prev => [...prev, result.photo!]);
            console.log('✅ Booking camera photo uploaded successfully:', result.photo.id);
          } else {
            console.error('❌ Booking camera photo upload failed:', result.error);
            alert(`Failed to upload photo: ${result.error}`);
          }
        } catch (error) {
          console.error('❌ Error processing booking camera photo:', error);
          alert('Failed to process photo');
        } finally {
          // Remove from uploading list
          setUploadingPhotos(prev => prev.filter(url => url !== blobUrl));
          // Clean up blob URL
          URL.revokeObjectURL(blobUrl);
        }
      }

      if (onPhotosUpdated) {
        onPhotosUpdated();
      }
    } finally {
      setUploading(false);
    }
  };

  const handleCameraClose = () => {
    setShowCamera(false);
  };

  const handleDeletePhoto = async (index: number) => {
    const photo = photos[index];
    if (!photo) return;

    console.log('📸 Deleting booking photo:', photo.id);
    
    try {
      const success = await deleteBookingPhoto(photo.id);
      if (success) {
        setPhotos(prev => prev.filter((_, i) => i !== index));
        console.log('✅ Booking photo deleted successfully');
        
        if (onPhotosUpdated) {
          onPhotosUpdated();
        }
      } else {
        alert('Failed to delete photo. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error deleting booking photo:', error);
      alert('Failed to delete photo. Please try again.');
    }
  };

  if (!isOpen) {
    return null;
  }

  console.log('📸 Rendering booking photo modal content with portal to document.body');
  const isMobile = window.innerWidth < 640;
  
  const modalContent = (
    <div 
      className="fixed inset-0 z-[99999] overflow-y-auto"
      style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0,
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
      }}
    >
      {/* Backdrop */}
      <div
        className={`fixed inset-0 transition-opacity duration-200 ${
          isAnimating ? 'bg-black/40' : 'bg-black/0'
        }`}
        style={{
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)'
        }}
        onClick={handleClose}
      />

      {/* Modal Content */}
      <div
        className={`relative bg-white text-left overflow-hidden transform transition-all duration-200 ${
          isMobile 
            ? `w-full h-full ${isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `inline-block align-middle bg-white rounded-[20px] shadow-2xl w-full max-w-4xl mx-auto my-8 ${
                isAnimating ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-4 opacity-0 scale-95'
              }`
        }`}
        style={{ 
          maxHeight: isMobile ? '100vh' : '90vh',
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - iOS Style */}
        <div className={`bg-white border-b border-gray-200/60 ${isMobile ? 'px-3 py-2.5' : 'px-6 py-4'}`}>
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <h3 className={`${isMobile ? 'text-[16px]' : 'text-xl'} font-semibold text-gray-900 truncate`} style={{ 
                letterSpacing: '-0.01em'
              }}>
                Vehicle Photos
              </h3>
              {!isMobile && (
                <p className="mt-0.5 text-[13px] text-gray-600 truncate">
                  {vehicleTitle}
                </p>
              )}
            </div>
            <button
              onClick={handleClose}
              className="h-8 w-8 flex items-center justify-center text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation flex-shrink-0 ml-2"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

          {/* Content */}
          <div className={`${isMobile ? 'px-3 py-3' : 'px-6 py-5'} overflow-y-auto`} style={{ 
            maxHeight: isMobile ? 'calc(100vh - 180px)' : 'calc(90vh - 200px)',
            WebkitOverflowScrolling: 'touch'
          }}>
            {/* Desktop: camera + library chooser. Mobile opens live camera directly (upload lives on camera UI). */}
            {isMobile ? (
              <div className="mb-4">
                <button
                  type="button"
                  onClick={handleCameraCapture}
                  disabled={uploading || showCamera}
                  className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-[12px] hover:bg-blue-700 active:bg-blue-800 active:scale-[0.98] focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 touch-manipulation shadow-sm"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <Camera className="h-5 w-5 mr-2" />
                  <span className="text-[15px] font-medium">Add Photos</span>
                </button>
              </div>
            ) : (
              <div className="mb-6 flex flex-col sm:flex-row gap-2.5">
                <button
                  type="button"
                  onClick={handleCameraCapture}
                  disabled={uploading || showCamera}
                  className="flex-1 flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-[12px] hover:bg-blue-700 active:bg-blue-800 active:scale-[0.98] focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 touch-manipulation shadow-sm"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <Camera className="h-5 w-5 mr-2" />
                  <span className="text-[15px] font-medium">{showCamera ? 'Camera Open...' : 'Open Camera'}</span>
                </button>

                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex-1 flex items-center justify-center px-4 py-3 bg-gray-100 text-gray-700 rounded-[12px] hover:bg-gray-200 active:bg-gray-300 active:scale-[0.98] focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <Upload className="h-5 w-5 mr-2" />
                  <span className="text-[15px] font-medium">Upload Photos</span>
                </button>
              </div>
            )}

            {/* Photo Grid - iOS Style */}
            {photos.length > 0 ? (
              <div className={`grid ${isMobile ? 'grid-cols-3' : 'grid-cols-3 lg:grid-cols-4'} gap-2`}>
                {photos.map((photo, index) => (
                  <div key={photo.id} className="relative group">
                    <div className="aspect-square rounded-[10px] overflow-hidden bg-gray-100">
                      <img
                        src={getBookingPhotoUrl(photo.file_path)}
                        alt={photo.file_name}
                        className="w-full h-full object-cover cursor-pointer"
                        onClick={() => openImageViewer({
                          images: photos.map((ph) => ({ src: getBookingPhotoUrl(ph.file_path), caption: ph.file_name })),
                          index,
                        })}
                        onError={(e) => {
                          console.error('❌ Failed to load booking photo:', {
                            photoId: photo.id,
                            filePath: photo.file_path,
                            fileName: photo.file_name,
                            url: getBookingPhotoUrl(photo.file_path)
                          });
                          (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDIwMCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMTI4IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMDAgNjRMMTE2IDQ4SDE4NFY4MEgxMTZMMTAwIDY0WiIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4K';
                          (e.target as HTMLImageElement).style.backgroundColor = '#f3f4f6';
                        }}
                        onLoad={() => {
                          console.log('✅ Successfully loaded booking photo:', photo.file_name);
                        }}
                      />
                    </div>
                    <button
                      onClick={() => handleDeletePhoto(index)}
                      className="absolute top-1.5 right-1.5 h-6 w-6 flex items-center justify-center bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 active:opacity-100 active:bg-red-600 transition-all duration-150 hover:bg-red-600 active:scale-95 touch-manipulation shadow-sm"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className={`text-center ${isMobile ? 'py-16' : 'py-12'} bg-gray-50/50 rounded-[12px] border border-gray-200/60`}>
                <Camera className={`${isMobile ? 'h-10 w-10' : 'h-12 w-12'} text-gray-400 mx-auto mb-3`} />
                <p className="text-[13px] text-gray-600 font-medium">No photos yet</p>
                <p className="text-[11px] text-gray-500 mt-1">Tap to capture or upload photos</p>
              </div>
            )}

            {/* Hidden file input — library only (no capture; capture forced camera on iOS) */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* Footer - iOS Style */}
          <div className={`bg-white border-t border-gray-200/60 ${isMobile ? 'px-3 py-3' : 'px-6 py-4'}`}>
            <button
              type="button"
              onClick={handleClose}
              className={`w-full inline-flex justify-center rounded-[12px] px-4 py-3 bg-blue-600 text-white font-semibold hover:bg-blue-700 active:bg-blue-800 active:scale-[0.98] focus:outline-none transition-all duration-150 touch-manipulation shadow-sm ${
                isMobile ? 'text-[15px]' : 'text-base'
              }`}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              Done {photos.length > 0 && `(${photos.length})`}
            </button>
          </div>
        </div>

        {/* Camera Capture Modal */}
        <CameraCapture
          isOpen={showCamera}
          onClose={handleCameraClose}
          onPhotosCapture={handleCameraPhotos}
          title={`Photos for "${vehicleTitle}"`}
        />
      </div>
  );

  return createPortal(modalContent, document.body);
}
